// ============================================================================
// PERMISSIONS SYSTEM - Matches Django Backend Groups & Permissions
// ============================================================================

/**
 * Get current user from memory (no localStorage)
 * In production, this would come from your auth context/state management
 */
let currentUserCache = null;

export const setCurrentUser = (user) => {
  currentUserCache = user;
};

export const getCurrentUser = () => {
  return currentUserCache;
};

export const clearCurrentUser = () => {
  currentUserCache = null;
};

/**
 * Extract user role from user object
 */
export const getUserRole = () => {
  const user = getCurrentUser();
  return user?.role || 'staff';
};

/**
 * Extract user groups from user object
 * Groups come from backend as array of objects: [{name: 'Sales Staff'}, ...]
 */
export const getUserGroups = () => {
  const user = getCurrentUser();
  if (!user?.groups) return [];
  
  // Handle both formats: array of strings or array of objects
  return user.groups.map(g => typeof g === 'string' ? g : g.name);
};

/**
 * Check if user has a specific role
 */
export const hasRole = (role) => {
  const userRole = getUserRole();
  return userRole === role;
};

/**
 * Check if user is superuser (platform admin)
 */
export const isSuperuser = () => {
  const user = getCurrentUser();
  return user?.is_superuser === true;
};

/**
 * Check if user is organization admin
 */
export const isOrgAdmin = () => {
  return isInGroup('Organization Admin');
};

/**
 * Check if user is manager
 */
export const isManager = () => {
  return isInGroup('Manager');
};

/**
 * Check if user is manager or above (includes Org Admin)
 */
export const isManagerOrAbove = () => {
  return isOrgAdmin() || isManager();
};

/**
 * Check if user belongs to a specific group
 */
export const isInGroup = (groupName) => {
  const groups = getUserGroups();
  return groups.includes(groupName);
};

/**
 * Check if user is in any of the provided groups
 */
export const isInAnyGroup = (groupNames) => {
  const groups = getUserGroups();
  return groupNames.some(name => groups.includes(name));
};

// ============================================================================
// WAREHOUSE FILTERING FUNCTIONS
// ============================================================================

/**
 * Get user's assigned warehouse
 */
export const getAssignedWarehouse = () => {
  const user = getCurrentUser();
  return user?.assigned_warehouse || null;
};

/**
 * Check if user is restricted to specific warehouse
 * Staff with assigned warehouse can only see that warehouse
 */
export const isWarehouseRestricted = () => {
  const user = getCurrentUser();
  // Only staff with assigned warehouse are restricted
  return user?.role === 'staff' && user?.assigned_warehouse;
};

/**
 * Filter warehouses based on user permissions
 * @param {Array} warehouses - All warehouses
 * @returns {Array} - Filtered warehouses
 */
export const filterWarehouses = (warehouses) => {
  const user = getCurrentUser();
  
  // Superusers and admins see all
  if (isSuperuser() || isOrgAdmin()) {
    return warehouses;
  }
  
  // Managers see all warehouses in their organization
  if (isManager()) {
    return warehouses;
  }
  
  // Staff with assigned warehouse only see their warehouse
  if (user?.assigned_warehouse) {
    return warehouses.filter(w => w.id === user.assigned_warehouse.id);
  }
  
  // Staff without assigned warehouse see nothing
  if (user?.role === 'staff') {
    return [];
  }
  
  // Default: show all (fallback)
  return warehouses;
};

/**
 * Check if user can access a specific warehouse
 */
export const canAccessWarehouse = (warehouseId) => {
  const user = getCurrentUser();
  
  // Superusers and admins can access all
  if (isSuperuser() || isOrgAdmin()) {
    return true;
  }
  
  // Managers can access all
  if (isManager()) {
    return true;
  }
  
  // Staff can only access their assigned warehouse
  if (user?.assigned_warehouse) {
    return user.assigned_warehouse.id === warehouseId;
  }
  
  return false;
};

// ============================================================================
// PERMISSION DEFINITIONS - Matches Django Backend
// ============================================================================

export const PERMISSIONS = {
  // Warehouses - Only admins can manage
  warehouses: {
    view: () => true, // Everyone can view
    create: () => isSuperuser() || isOrgAdmin(),
    edit: () => isSuperuser() || isOrgAdmin(),
    delete: () => isSuperuser() || isOrgAdmin(),
  },
  
  // Products - Managers and Inventory Clerks can manage
  products: {
    view: () => true,
    create: () => isSuperuser() || isManagerOrAbove() || isInGroup('Inventory Clerk'),
    edit: () => isSuperuser() || isManagerOrAbove() || isInGroup('Inventory Clerk'),
    delete: () => isSuperuser() || isOrgAdmin(),
  },
  
  // Stock - Managers and Inventory Clerks can manage
  stocks: {
    view: () => true,
    create: () => isSuperuser() || isManagerOrAbove() || isInGroup('Inventory Clerk'),
    edit: () => isSuperuser() || isManagerOrAbove() || isInGroup('Inventory Clerk'),
    delete: () => isSuperuser() || isManagerOrAbove(),
  },
  
  // Customers - Sales Staff and Managers can manage
  customers: {
    view: () => true,
    create: () => isSuperuser() || isManagerOrAbove() || isInGroup('Sales Staff'),
    edit: () => isSuperuser() || isManagerOrAbove() || isInGroup('Sales Staff'),
    delete: () => isSuperuser() || isManagerOrAbove(),
  },
  
  // Orders - Sales Staff and Managers can manage
  orders: {
    view: () => true,
    create: () => isSuperuser() || isManagerOrAbove() || isInGroup('Sales Staff'),
    edit: () => isSuperuser() || isManagerOrAbove() || isInGroup('Sales Staff'),
    delete: () => isSuperuser() || isManagerOrAbove(),
    changeStatus: () => isSuperuser() || isManagerOrAbove() || isInGroup('Sales Staff'),
  },
  
  // Users - Only admins can manage users
  users: {
    view: () => isSuperuser() || isOrgAdmin(),
    create: () => isSuperuser() || isOrgAdmin(),
    edit: () => isSuperuser() || isOrgAdmin(),
    delete: () => isSuperuser() || isOrgAdmin(),
  },
  
  // Analytics - Managers and Accountants
  analytics: {
    view: () => isSuperuser() || isManagerOrAbove() || isInGroup('Accountant'),
    export: () => isSuperuser() || isManagerOrAbove(),
  },
  
  // Reports - Managers and Accountants
  reports: {
    view: () => isSuperuser() || isManagerOrAbove() || isInGroup('Accountant'),
    generate: () => isSuperuser() || isManagerOrAbove(),
    export: () => isSuperuser() || isManagerOrAbove(),
  },
  
  // Stock Transfers - Inventory and Managers
  stockTransfers: {
    view: () => true,
    create: () => isSuperuser() || isManagerOrAbove() || isInGroup('Inventory Clerk'),
    edit: () => isSuperuser() || isManagerOrAbove() || isInGroup('Inventory Clerk'),
    delete: () => isSuperuser() || isManagerOrAbove(),
  },
  
  // Predictions - View only for authorized users
  predictions: {
    view: () => isSuperuser() || isManagerOrAbove() || isInGroup('Accountant'),
  },
  
  // Sales Metrics - View only for authorized users
  salesMetrics: {
    view: () => isSuperuser() || isManagerOrAbove() || isInGroup('Accountant'),
  },
};

// ============================================================================
// PERMISSION CHECK FUNCTIONS
// ============================================================================

/**
 * Check if user can perform an action on a feature
 * @param {string} feature - Feature name (e.g., 'products', 'orders')
 * @param {string} action - Action name (e.g., 'view', 'create', 'edit', 'delete')
 * @returns {boolean}
 */
export const canPerform = (feature, action) => {
  const user = getCurrentUser();
  if (!user) return false;
  
  // Superusers can do everything
  if (user.is_superuser) return true;
  
  const permission = PERMISSIONS[feature]?.[action];
  return permission ? permission() : false;
};

/**
 * Check if user can perform ANY of the specified actions
 */
export const canPerformAny = (feature, actions) => {
  return actions.some(action => canPerform(feature, action));
};

/**
 * Check if user can perform ALL of the specified actions
 */
export const canPerformAll = (feature, actions) => {
  return actions.every(action => canPerform(feature, action));
};

// ============================================================================
// REACT HOOK FOR PERMISSIONS (for use with React components)
// ============================================================================

/**
 * Hook to get permissions for a feature
 * @param {string} feature - Feature name
 * @returns {object} - Permission object with helper methods
 */
export const usePermissions = (feature) => {
  const user = getCurrentUser();
  
  return {
    canView: canPerform(feature, 'view'),
    canCreate: canPerform(feature, 'create'),
    canEdit: canPerform(feature, 'edit'),
    canDelete: canPerform(feature, 'delete'),
    canExport: canPerform(feature, 'export'),
    canGenerate: canPerform(feature, 'generate'),
    canChangeStatus: canPerform(feature, 'changeStatus'),
    
    // User info
    user,
    isSuperuser: isSuperuser(),
    isOrgAdmin: isOrgAdmin(),
    isManager: isManager(),
    isManagerOrAbove: isManagerOrAbove(),
    groups: getUserGroups(),
    role: getUserRole(),
  };
};

// ============================================================================
// PERMISSION GROUPS REFERENCE (matches backend)
// ============================================================================

export const PERMISSION_GROUPS = {
  'Organization Admin': {
    description: 'Full access to organization data, can manage users',
    features: [
      'All warehouses, products, stock, customers, orders',
      'User management (create, edit users)',
      'Analytics and reports',
      'Stock transfers'
    ]
  },
  'Manager': {
    description: 'Operational management, no user administration',
    features: [
      'View warehouses',
      'Manage products, stock, customers, orders',
      'Analytics and reports',
      'Stock transfers'
    ]
  },
  'Sales Staff': {
    description: 'Create orders, manage customers, view products/stock',
    features: [
      'Create and manage customers',
      'Create and manage orders',
      'View products and stock (for availability)',
      'View warehouses'
    ]
  },
  'Inventory Clerk': {
    description: 'Manage stock and products, no sales access',
    features: [
      'Create and manage products',
      'Manage stock levels',
      'View warehouses',
      'Stock transfers'
    ]
  },
  'Accountant': {
    description: 'View-only access for reporting',
    features: [
      'View all data (warehouses, products, stock, customers, orders)',
      'Analytics and reports (view only)'
    ]
  }
};

// ============================================================================
// NAVIGATION VISIBILITY HELPER
// ============================================================================

/**
 * Get visible navigation items based on permissions
 */
export const getVisibleNavItems = () => {
  const items = [];
  
  // Dashboard - always visible
  items.push({ name: 'Dashboard', path: '/dashboard', icon: 'LayoutDashboard' });
  
  // Products - visible to everyone
  if (canPerform('products', 'view')) {
    items.push({ name: 'Products', path: '/products', icon: 'Package' });
  }
  
  // Stock - visible to everyone
  if (canPerform('stocks', 'view')) {
    items.push({ name: 'Stock', path: '/stock', icon: 'Database' });
  }
  
  // Customers - visible to sales and managers
  if (canPerform('customers', 'view')) {
    items.push({ name: 'Customers', path: '/customers', icon: 'Users' });
  }
  
  // Orders - visible to sales and managers
  if (canPerform('orders', 'view')) {
    items.push({ name: 'Orders', path: '/orders', icon: 'ShoppingCart' });
  }
  
  // Warehouses - visible to everyone (view-only for most)
  if (canPerform('warehouses', 'view')) {
    items.push({ name: 'Warehouses', path: '/warehouses', icon: 'Warehouse' });
  }
  
  // Analytics - visible to managers and accountants
  if (canPerform('analytics', 'view')) {
    items.push({ name: 'Analytics', path: '/analytics', icon: 'TrendingUp' });
  }
  
  // Reports - visible to managers and accountants
  if (canPerform('reports', 'view')) {
    items.push({ name: 'Reports', path: '/reports', icon: 'FileText' });
  }
  
  // Users - visible only to admins
  if (canPerform('users', 'view')) {
    items.push({ name: 'Users', path: '/users', icon: 'UserCog' });
  }
  
  return items;
};

// ============================================================================
// DEBUG HELPER
// ============================================================================

/**
 * Log current user permissions to console (for debugging)
 */
export const logPermissions = () => {
  const user = getCurrentUser();
  
  Object.keys(PERMISSIONS).forEach(feature => {
    // console.log(`\n${feature.toUpperCase()}:`);
    Object.keys(PERMISSIONS[feature]).forEach(action => {
      const can = canPerform(feature, action);
    });
  });
  
  const navItems = getVisibleNavItems();
  // navItems.forEach(item => console.log(`  • ${item.name}`));
  
};

// ============================================================================
// EXPORTS
// ============================================================================

export default {
  // User management
  setCurrentUser,
  getCurrentUser,
  clearCurrentUser,
  getUserRole,
  getUserGroups,
  
  // Role checks
  hasRole,
  isSuperuser,
  isOrgAdmin,
  isManager,
  isManagerOrAbove,
  isInGroup,
  isInAnyGroup,
  
  // Warehouse filtering
  getAssignedWarehouse,
  isWarehouseRestricted,
  filterWarehouses,
  canAccessWarehouse,
  
  // Permission checks
  canPerform,
  canPerformAny,
  canPerformAll,
  
  // React hook
  usePermissions,
  
  // Helpers
  getVisibleNavItems,
  logPermissions,
  
  // Constants
  PERMISSIONS,
  PERMISSION_GROUPS,
};
